import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Button, EmptyState, PageBody } from '@wundo/ui';
import { PageTitle } from '@/components/PageTitle.tsx';
import { NoCalendarEmptyState } from '@/components/NoCalendarEmptyState.tsx';
import { ConversationList } from '@/components/conversations/ConversationList.tsx';
import {
  ConversationsToolbar,
  type ConversationKindFilter,
} from '@/components/conversations/ConversationsToolbar.tsx';
import { groupByMonth } from '@/components/conversations/groupByMonth.ts';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import {
  fetchConversations,
  type ConversationsPage as ConversationsPageData,
} from '@/lib/conversations.ts';
import { useConnectedCalendars } from '@/lib/useConnectedCalendars.ts';
import { usePublishChatPageOffer } from '@/lib/chatPageOffer.ts';
import { useChatPageNudge } from '@/lib/useChatPageNudge.ts';
import { m } from '@/paraglide/messages.js';

export const Route = createFileRoute('/_app/conversations')({
  component: ConversationsPage,
});

/* Where the drawer beside this page is standing, for the nudge it asks for. */
const NUDGE_PAGE = { page: 'conversations' } as const;

/**
 * How long typing settles before the list is asked again. Long enough that a
 * name is typed in one request, short enough to feel like the list is answering.
 */
const SEARCH_SETTLES_MS = 300;

/**
 * The conversations Wundo accompanies, newest first, past and planned together.
 *
 * A conversation is here because Wundo takes part in it: a past meeting has a
 * verslag to show for it, a planned one is a meeting Wundo will be at. That is
 * the whole inclusion rule, which is why the page has no way to create one: it
 * is the calendar and what Wundo captured that put a conversation on this list.
 *
 * Searching and the kind filter happen on the server, unlike the team page's:
 * this is a history that keeps growing, and narrowing the pages already in the
 * browser would quietly leave the older matches out — and page a list that is
 * mostly hidden, so a page of thirty could show three rows.
 *
 * A row opens the conversation's own page, the way a card on the agenda does.
 * Both pages are lists of the same thing, and a click that means one thing on
 * one of them and another on the other is a difference the user has to learn
 * for no reason.
 */
export function ConversationsPage() {
  /* The one thing worth recommending here to somebody new (DEV-72). */
  const nudge = useChatPageNudge(NUDGE_PAGE);

  /* This page's conversation starters (DEV-16). */
  usePublishChatPageOffer({
    quickActions: [
      { text: m.chat_quick_conversations_recurring(), icon: 'recap' },
      { text: m.chat_quick_conversations_report_missing(), icon: 'check' },
    ],
    nudge,
  });

  const [search, setSearch] = useState('');
  /* What has actually been asked for. Every change is a new list, from page 1. */
  const [query, setQuery] = useState('');
  /* Which kind of conversation to look through. Unlike typing, this is instant. */
  const [kind, setKind] = useState<ConversationKindFilter>('all');
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search), SEARCH_SETTLES_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  /* Whether there is a calendar at all — which empty page to show turns on it. */
  const { noCalendar } = useConnectedCalendars();

  const { data, isPending, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['conversations', user?.id, query, kind],
      queryFn: ({ pageParam }) =>
        fetchConversations(user!.id, {
          search: query,
          kind: kind === 'all' ? undefined : kind,
          page: pageParam,
        }),
      initialPageParam: 1,
      getNextPageParam: (page: ConversationsPageData) => page.next,
      enabled: Boolean(user?.id),
    });

  const months = useMemo(
    () => groupByMonth((data?.pages ?? []).flatMap((page) => page.conversations)),
    [data],
  );

  const empty = !isPending && !isError && months.length === 0;
  /* Whether the user narrowed the list themselves — which empty page to show. */
  const narrowed = query !== '' || kind !== 'all';

  return (
    <>
      <PageTitle>{m.nav_conversations()}</PageTitle>
      <PageBody>
        <ConversationsToolbar
          search={search}
          onSearchChange={setSearch}
          kind={kind}
          onKindChange={setKind}
        />

        <div className='mt-6'>
          {isPending && (
            <p className='text-base text-ink-muted'>{m.conversations_loading()}</p>
          )}
          {isError && <p className='text-base text-danger'>{m.conversations_failed()}</p>}

          {/*
            A search or a kind with no hits is not an empty archive: it says so
            in its own words, and never sends somebody off to connect a calendar
            they have. The search is named when there is one, because that is
            what the user last did and the wording they will recognise.
          */}
          {empty && narrowed && (
            <EmptyState
              title={m.conversations_search_empty_title()}
              description={
                query !== ''
                  ? m.conversations_search_empty_body()
                  : m.conversations_kind_empty_body()
              }
            />
          )}

          {empty &&
            !narrowed &&
            (noCalendar ? (
              <NoCalendarEmptyState
                userId={user!.id}
                title={m.conversations_empty_no_calendar_title()}
                description={m.conversations_empty_no_calendar_body()}
              />
            ) : (
              <EmptyState
                title={m.conversations_empty_title()}
                description={m.conversations_empty_body()}
              />
            ))}

          <ConversationList
            months={months}
            onSelect={(conversation) =>
              void navigate({
                to: '/meetings/$meetingId',
                params: { meetingId: conversation.id },
              })
            }
          />

          {hasNextPage && (
            <div className='mt-3 flex justify-center'>
              <Button
                variant='ghost'
                size='sm'
                disabled={isFetchingNextPage}
                onClick={() => void fetchNextPage()}
              >
                {m.conversations_show_more()}
              </Button>
            </div>
          )}
        </div>
      </PageBody>
    </>
  );
}
